import {
  Button,
  CircularProgress,
  FormControl,
  Grid2,
  MenuItem,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { GKDatePicker, GKSelect, GKTextField } from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import {
  BOND_COUPON_FREQUENCY,
  COMPANY_CAP,
  COMPANY_ETF_SUB_TYPE,
  COMPANY_INSTTYPE,
  COMPANY_STATUS,
  INSTRUMENT_SUB_TYPE,
  MUTUAL_FUND_SUB_TYPE,
} from "utils/constants";

function FundamentalDetails({ companyDetails, refetch }: any) {
  const { id } = useParams();

  const {
    state: { currentUser },
  } = useAppContext();

  const isDisabled = currentUser?.phone === "9911223344";

  // const formik = useFormik({
  //   initialValues: {
  //     cap: companyDetails?.cap || "",
  //     finCode: companyDetails?.finCode || "",
  //     house: companyDetails?.house || "",
  //     industry: companyDetails?.industry || "",
  //     instType: companyDetails?.instType || "",
  //     kiteCode: companyDetails?.kiteCode || "",
  //     name: companyDetails?.name || "",
  //     sector: companyDetails?.sector || "",
  //     shortName: companyDetails?.shortName || "",
  //     status: companyDetails?.status || "",
  //     etfSubType: companyDetails?.etfSubType || "NA",
  //     mfSubType: companyDetails?.mfSubType || "",
  //     instSubType: companyDetails?.instSubType || "",
  //   },
  //   onSubmit: async (values) => {
  //     await axiosInstance
  //       .put(`/admin-instrument/${id}/update/`, values)
  //       .then((res) => {
  //         toast.success(res.data.message);
  //         refetch();
  //       })
  //       .catch((error) =>
  //         toast.error(error?.response?.data?.message || "Something went wrong")
  //       );
  //   },
  // });

  const { mutate, isLoading } = useMutation({
    mutationKey: ["UPDATE_COMPANY", id],
    mutationFn: (data: any) =>
      axiosInstance.put(`/admin-instrument/${id}/update/`, data),
    onSuccess: (res) => {
      toast.success(res.data.message);
      refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik({
    initialValues: {
      cap: companyDetails?.cap || "",
      finCode: companyDetails?.finCode || "",
      house: companyDetails?.house || "",
      industry: companyDetails?.industry || "",
      instType: companyDetails?.instType || "",
      kiteCode: companyDetails?.kiteCode || "",
      name: companyDetails?.name || "",
      sector: companyDetails?.sector || "",
      shortName: companyDetails?.shortName || "",
      status: companyDetails?.status || "",
      etfSubType: companyDetails?.etfSubType || "NA",
      mfSubType: companyDetails?.mfSubType || "",
      instSubType: companyDetails?.instSubType || "",
      startDate: companyDetails.startDate ? companyDetails.startDate : null,
      expiryDate: companyDetails.expiryDate ? companyDetails.expiryDate : null,
      faceValue: companyDetails?.faceValue ?? "",
      couponRate: companyDetails?.couponRate ?? "",
      couponFrequency: companyDetails?.couponFrequency || "",
      issueDate: companyDetails?.issueDate || null,
      maturityDate: companyDetails?.maturityDate || null,
      lastInterestDate: companyDetails?.lastInterestDate || null,
      nextInterestDate: companyDetails?.nextInterestDate || null,
    },
    onSubmit: (values) => {
      const {
        faceValue,
        couponRate,
        couponFrequency,
        issueDate,
        maturityDate,
        lastInterestDate,
        nextInterestDate,
        ...rest
      } = values;

      const payload = {
        ...rest,
        startDate: values.startDate
          ? dayjs(values.startDate).format("YYYY-MM-DD")
          : null,
        expiryDate: values.expiryDate
          ? dayjs(values.expiryDate).format("YYYY-MM-DD")
          : null,
        // Bond terms are rejected by the backend for any other instrument type.
        ...(values.instType === "BOND" && {
          faceValue: faceValue === "" ? null : Number(faceValue),
          couponFrequency: couponFrequency || null,
          couponRate:
            couponFrequency === "AT_MATURITY" || couponRate === ""
              ? null
              : Number(couponRate),
          issueDate,
          maturityDate,
          lastInterestDate:
            couponFrequency === "AT_MATURITY" ? null : lastInterestDate,
          nextInterestDate:
            couponFrequency === "AT_MATURITY" ? null : nextInterestDate,
        }),
      };
      mutate(payload);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} id="fundamental-details">
      <Grid2 container spacing={2}>
        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="Name"
              onChange={formik?.handleChange}
              value={formik.values.name}
              name="name"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="Sector"
              onChange={formik?.handleChange}
              value={formik.values.sector}
              name="sector"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="Short Name"
              onChange={formik?.handleChange}
              value={formik.values.shortName}
              name="shortName"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="Industry"
              onChange={formik?.handleChange}
              value={formik.values.industry}
              name="industry"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="FinCode"
              onChange={formik?.handleChange}
              value={formik.values.finCode}
              name="finCode"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="House"
              onChange={formik?.handleChange}
              value={formik.values.house}
              name="house"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKSelect
              inputLabel="Insta Type"
              onChange={formik?.handleChange}
              value={formik.values.instType}
              name="instType"
              disabled={isDisabled}
            >
              {COMPANY_INSTTYPE.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKDatePicker
              inputLabel="Start Date"
              name="startDate"
              value={
                formik.values.startDate
                  ? dayjs(formik.values.startDate, "YYYY-MM-DD")
                  : null
              }
              onChange={(value) => {
                formik.setFieldValue(
                  "startDate",
                  value ? dayjs(value).format("YYYY-MM-DD") : null
                );
              }}
              minDate={dayjs("2010-01-01")}
              maxDate={dayjs()}
              disableFuture
              slotProps={{
                textField: {
                  error: false,
                  helperText: false,
                },
              }}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKDatePicker
              inputLabel="End Date"
              name="expiryDate"
              value={
                formik.values.expiryDate
                  ? dayjs(formik.values.expiryDate, "YYYY-MM-DD")
                  : null
              }
              onChange={(value) => {
                formik.setFieldValue(
                  "expiryDate",
                  value ? dayjs(value).format("YYYY-MM-DD") : null
                );
              }}
              minDate={dayjs("2010-01-01")}
              maxDate={dayjs()}
              disableFuture
              slotProps={{
                textField: {
                  error: false,
                  helperText: false,
                },
              }}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKTextField
              inputLabel="KiteCode"
              onChange={formik?.handleChange}
              value={formik.values.kiteCode}
              name="kiteCode"
              disabled={isDisabled}
            />
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKSelect
              inputLabel="Cap"
              onChange={formik?.handleChange}
              value={formik.values.cap}
              name="cap"
              disabled={isDisabled}
            >
              {COMPANY_CAP.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKSelect
              inputLabel="Status"
              onChange={formik?.handleChange}
              value={formik.values.status}
              name="status"
              disabled={isDisabled}
            >
              {COMPANY_STATUS.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        </Grid2>

        {formik.values.instType === "ETF" && (
          <Grid2 size={6}>
            <FormControl variant="standard" fullWidth>
              <GKSelect
                inputLabel="ETF Sub Type"
                onChange={formik?.handleChange}
                value={formik.values.etfSubType}
                name="etfSubType"
                disabled={isDisabled}
              >
                {COMPANY_ETF_SUB_TYPE.map((dataItem, i) => (
                  <MenuItem key={i} value={dataItem.value}>
                    {dataItem.name}
                  </MenuItem>
                ))}
              </GKSelect>
            </FormControl>
          </Grid2>
        )}

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKSelect
              inputLabel="Mutual Funds Sub Type"
              onChange={formik?.handleChange}
              value={formik.values.mfSubType}
              name="mfSubType"
              disabled={isDisabled}
            >
              {MUTUAL_FUND_SUB_TYPE.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        </Grid2>

        <Grid2 size={6}>
          <FormControl variant="standard" fullWidth>
            <GKSelect
              inputLabel="Instrument Sub Type"
              onChange={formik?.handleChange}
              value={formik.values.instSubType}
              name="Insta Sub Type"
              disabled={isDisabled}
            >
              {INSTRUMENT_SUB_TYPE.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        </Grid2>

        {formik.values.instType === "BOND" && (
          <>
            <Grid2 size={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  inputLabel="Face Value"
                  type="number"
                  onChange={formik?.handleChange}
                  value={formik.values.faceValue}
                  name="faceValue"
                  disabled={isDisabled}
                />
              </FormControl>
            </Grid2>

            <Grid2 size={6}>
              <FormControl variant="standard" fullWidth>
                <GKSelect
                  inputLabel="Coupon Frequency"
                  onChange={formik?.handleChange}
                  value={formik.values.couponFrequency}
                  name="couponFrequency"
                  disabled={isDisabled}
                >
                  {BOND_COUPON_FREQUENCY.map((dataItem, i) => (
                    <MenuItem key={i} value={dataItem.value}>
                      {dataItem.name}
                    </MenuItem>
                  ))}
                </GKSelect>
              </FormControl>
            </Grid2>

            {formik.values.couponFrequency !== "AT_MATURITY" && (
              <Grid2 size={6}>
                <FormControl variant="standard" fullWidth>
                  <GKTextField
                    inputLabel="Coupon Rate (%)"
                    type="number"
                    onChange={formik?.handleChange}
                    value={formik.values.couponRate}
                    name="couponRate"
                    disabled={isDisabled}
                  />
                </FormControl>
              </Grid2>
            )}

            <Grid2 size={6}>
              <FormControl variant="standard" fullWidth>
                <GKDatePicker
                  inputLabel="Issue Date"
                  name="issueDate"
                  value={
                    formik.values.issueDate
                      ? dayjs(formik.values.issueDate, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(value) => {
                    formik.setFieldValue(
                      "issueDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : null
                    );
                  }}
                  disabled={isDisabled}
                />
              </FormControl>
            </Grid2>

            <Grid2 size={6}>
              <FormControl variant="standard" fullWidth>
                <GKDatePicker
                  inputLabel="Maturity Date"
                  name="maturityDate"
                  value={
                    formik.values.maturityDate
                      ? dayjs(formik.values.maturityDate, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(value) => {
                    formik.setFieldValue(
                      "maturityDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : null
                    );
                  }}
                  disabled={isDisabled}
                />
              </FormControl>
            </Grid2>

            {formik.values.couponFrequency !== "AT_MATURITY" && (
              <>
                <Grid2 size={6}>
                  <FormControl variant="standard" fullWidth>
                    <GKDatePicker
                      inputLabel="Last Interest Date"
                      name="lastInterestDate"
                      value={
                        formik.values.lastInterestDate
                          ? dayjs(formik.values.lastInterestDate, "YYYY-MM-DD")
                          : null
                      }
                      onChange={(value) => {
                        formik.setFieldValue(
                          "lastInterestDate",
                          value ? dayjs(value).format("YYYY-MM-DD") : null
                        );
                      }}
                      disabled={isDisabled}
                    />
                  </FormControl>
                </Grid2>

                <Grid2 size={6}>
                  <FormControl variant="standard" fullWidth>
                    <GKDatePicker
                      inputLabel="Next Interest Date"
                      name="nextInterestDate"
                      value={
                        formik.values.nextInterestDate
                          ? dayjs(formik.values.nextInterestDate, "YYYY-MM-DD")
                          : null
                      }
                      onChange={(value) => {
                        formik.setFieldValue(
                          "nextInterestDate",
                          value ? dayjs(value).format("YYYY-MM-DD") : null
                        );
                      }}
                      disabled={isDisabled}
                    />
                  </FormControl>
                </Grid2>
              </>
            )}
          </>
        )}

        <Grid2 textAlign={"right"} size={12}>
          <Button
            variant="contained"
            size="large"
            type="submit"
            disabled={isDisabled}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Grid2>
      </Grid2>
    </form>
  );
}

export default FundamentalDetails;
